// src/services/azurePendenzenService.js
// Azure Table Storage Service für Projekt-Pendenzen (Pendenzen, Entscheide, Notizen)
// Muster: azureProjektstandService.js (projektbezogene Tabelle, PartitionKey = projectUuid,
// do/while-Paginierung, automatische Tabellenerstellung beim ersten Aufruf).
import { sanitizeRichText } from "../utils/richText";

const PENDENZEN_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectPendenzen",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

const getBaseUrl = () =>
  `https://${PENDENZEN_CONFIG.storageAccount}.table.core.windows.net/${PENDENZEN_CONFIG.tableName}`;

// Eindeutige RowKey (GUID-artig) pro Eintrag
const generatePendenzId = () =>
  `pend_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const generateCategoryId = () =>
  `cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Allgemeine (projektübergreifende) Pendenzenlisten als Pseudo-Projekte.
// "Admin-GL" nur für die Geschäftsleitung (MF-Admin), "Team" für alle.
export const SPECIAL_LISTS = [
  { uuid: "__admin-gl", code: "admin-gl", title: "Admin-GL", adminOnly: true },
  { uuid: "__team", code: "team", title: "Team", adminOnly: false },
];

// Fixe Standard-Kategorien (bleiben immer vorhanden; projektspezifische kommen dazu)
export const FIXED_CATEGORIES = [
  { id: "Org", label: "Organisation", fixed: true },
  { id: "Pl", label: "Planung", fixed: true },
  { id: "Ko", label: "Kosten", fixed: true },
  { id: "Te", label: "Termine", fixed: true },
  { id: "Div", label: "Diverses", fixed: true },
];

const cleanValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

// Stellt sicher, dass die Tabelle existiert (Anlage beim ersten Aufruf).
// Cached pro Session, damit nicht bei jedem Aufruf ein POST abgesetzt wird.
let tableEnsured = false;
const ensureTableExists = async () => {
  if (tableEnsured) return true;
  try {
    const url = `https://${PENDENZEN_CONFIG.storageAccount}.table.core.windows.net/Tables?${PENDENZEN_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify({ TableName: PENDENZEN_CONFIG.tableName }),
    });
    // 409 = Tabelle existiert bereits, das ist in Ordnung
    if (response.ok || response.status === 409) {
      tableEnsured = true;
      return true;
    }
    return false;
  } catch (error) {
    console.error("Pendenzen: Tabelle konnte nicht angelegt werden:", error);
    return false;
  }
};

// Azure-Entität in Frontend-Objekt umwandeln
const mapEntity = (item) => ({
  id: item.RowKey,
  projectUuid: item.PartitionKey,
  nummer: parseInt(item.Nummer, 10) || 0,
  typ: item.Typ || "P",
  kategorie: item.Kategorie || "Div",
  beschreibung: item.Beschreibung || "",
  verantwortlich: item.Verantwortlich || "",
  // Externer Anzeigetext (Read-only-Share): Team-Mitglieder erscheinen nur als
  // "Malheur&Fortuna" (Namen werden nach aussen nicht gezeigt). Fallback auf den
  // internen Text für Altdaten ohne separates Feld.
  verantwortlichExtern: item.VerantwortlichExtern || item.Verantwortlich || "",
  // IDs der verantwortlichen Personen (projectPersons), US-PEND-002.
  // Frei erfasster Text aus US-PEND-001 bleibt in `verantwortlich` erhalten.
  verantwortlichIds: (() => {
    try {
      return item.VerantwortlichIds ? JSON.parse(item.VerantwortlichIds) : [];
    } catch {
      return [];
    }
  })(),
  terminSoll: item.TerminSoll || "",
  terminNeu: item.TerminNeu || "",
  status: item.Status || "offen",
  erledigtAm: item.ErledigtAm || "",
  kommentar: item.Kommentar || "",
  // Einfache Checkliste: Array von { id, text, done }
  checkliste: (() => {
    try {
      return item.Checkliste ? JSON.parse(item.Checkliste) : [];
    } catch {
      return [];
    }
  })(),
  sichtbarExtern:
    item.SichtbarExtern === true || item.SichtbarExtern === "true",
  // Freie Themen-Tags (z.B. Brandschutz, Statik, Behörden); ein oder mehrere.
  themen: (() => {
    try {
      return item.Themen ? JSON.parse(item.Themen) : [];
    } catch {
      return [];
    }
  })(),
  // Zuordnung zu einem Sitzungsgefäss (US-SITZ-001); leer = keiner Sitzung zugeordnet.
  sitzungId: item.SitzungId || "",
  // Zuordnung zu einer Folie/einem Zwischenraum der Sitzungspräsentation
  // (US-SITZ-003, ADR 0059): "slide:<folieId>" | "gap:<folieId|'start'>" | "".
  praesentationAnchor: item.PraesentationAnchor || "",
  erstelltAm: item.ErstelltAm || "",
  erstelltVon: item.ErstelltVon || "",
  linkedTodoIds: (() => {
    try {
      return item.LinkedTodoIds ? JSON.parse(item.LinkedTodoIds) : [];
    } catch {
      return [];
    }
  })(),
});

// Frontend-Objekt in Azure-Entität umwandeln
const toEntity = (projectUuid, rowKey, data) => ({
  PartitionKey: projectUuid,
  RowKey: rowKey,
  Nummer: data.nummer != null ? parseInt(data.nummer, 10) : 0,
  Typ: cleanValue(data.typ || "P"),
  Kategorie: cleanValue(data.kategorie || "Div"),
  // US-PEND-010: Beschreibung ist seit dem Rich-Text-Editor HTML statt reinem
  // Text – hier (statt nur beim Rendern) sanitisieren, analog
  // azureResearchService.js `AnswerHtml`. Altdaten ohne Tags bleiben unverändert.
  Beschreibung: cleanValue(sanitizeRichText(data.beschreibung)),
  Verantwortlich: cleanValue(data.verantwortlich),
  // Externer Anzeigetext für den Read-only-Share-Microservice (US-PEND-004/006):
  // dieser MUSS VerantwortlichExtern (nicht Verantwortlich) anzeigen, damit Namen
  // einzelner Team-Mitglieder extern verborgen bleiben.
  VerantwortlichExtern: cleanValue(
    data.verantwortlichExtern != null ? data.verantwortlichExtern : data.verantwortlich
  ),
  VerantwortlichIds: JSON.stringify(
    Array.isArray(data.verantwortlichIds) ? data.verantwortlichIds : []
  ),
  TerminSoll: cleanValue(data.terminSoll),
  TerminNeu: cleanValue(data.terminNeu),
  Status: cleanValue(data.status || "offen"),
  ErledigtAm: cleanValue(data.erledigtAm),
  Kommentar: cleanValue(data.kommentar),
  Checkliste: JSON.stringify(
    Array.isArray(data.checkliste) ? data.checkliste : []
  ),
  Themen: JSON.stringify(Array.isArray(data.themen) ? data.themen : []),
  SichtbarExtern: data.sichtbarExtern === true,
  SitzungId: cleanValue(data.sitzungId),
  PraesentationAnchor: cleanValue(data.praesentationAnchor),
  ErstelltAm: cleanValue(data.erstelltAm),
  ErstelltVon: cleanValue(data.erstelltVon),
  LinkedTodoIds: JSON.stringify(
    Array.isArray(data.linkedTodoIds) ? data.linkedTodoIds : []
  ),
});

// Alle Pendenzen eines Projekts laden (vollständig paginiert)
export const getPendenzen = async (projectUuid) => {
  if (!projectUuid) return [];
  try {
    await ensureTableExists();

    const filter = `&$filter=PartitionKey eq '${projectUuid}'`;
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${getBaseUrl()}()?${PENDENZEN_CONFIG.sasToken}${filter}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries
      .filter((e) => (e.RowKey || "").startsWith("pend_"))
      .map(mapEntity)
      .sort((a, b) => a.nummer - b.nummer);
  } catch (error) {
    console.error("Fehler beim Laden der Pendenzen:", error);
    throw new Error("Die Pendenzen konnten nicht geladen werden.");
  }
};

// Kategorie-Entität <-> Frontend
const mapCategory = (item) => ({
  id: item.RowKey,
  projectUuid: item.PartitionKey,
  label: item.Label || "",
  sortOrder: parseInt(item.SortOrder, 10) || 0,
  fixed: false,
});

// Projektspezifische Kategorien laden (nur cat_-Zeilen)
export const getCategories = async (projectUuid) => {
  if (!projectUuid) return [];
  try {
    await ensureTableExists();
    const filter = `&$filter=PartitionKey eq '${projectUuid}'`;
    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${getBaseUrl()}()?${PENDENZEN_CONFIG.sasToken}${filter}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }
      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });
      if (!response.ok) throw new Error(`Azure Table Error: ${response.status}`);
      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);
      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries
      .filter((e) => (e.RowKey || "").startsWith("cat_"))
      .map(mapCategory)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  } catch (error) {
    console.error("Fehler beim Laden der Kategorien:", error);
    throw new Error("Die Kategorien konnten nicht geladen werden.");
  }
};

// Neue projektspezifische Kategorie anlegen
export const createCategory = async (projectUuid, { label, sortOrder = 0 }) => {
  try {
    await ensureTableExists();
    const rowKey = generateCategoryId();
    const entity = {
      PartitionKey: projectUuid,
      RowKey: rowKey,
      Kind: "category",
      Label: cleanValue(label),
      SortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
    };
    const response = await fetch(`${getBaseUrl()}?${PENDENZEN_CONFIG.sasToken}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });
    if (!response.ok) throw new Error(`Azure Save Error: ${response.status}`);
    return mapCategory(entity);
  } catch (error) {
    console.error("Fehler beim Anlegen der Kategorie:", error);
    throw new Error("Die Kategorie konnte nicht angelegt werden.");
  }
};

// Kategorie umbenennen / neu sortieren
export const updateCategory = async (projectUuid, id, { label, sortOrder }) => {
  try {
    const entity = {
      PartitionKey: projectUuid,
      RowKey: id,
      Kind: "category",
      Label: cleanValue(label),
      SortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
    };
    const url = `${getBaseUrl()}(PartitionKey='${projectUuid}',RowKey='${id}')?${PENDENZEN_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });
    if (!response.ok) throw new Error(`Azure Update Error: ${response.status}`);
    return mapCategory(entity);
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Kategorie:", error);
    throw new Error("Die Kategorie konnte nicht aktualisiert werden.");
  }
};

// Kategorie löschen
export const deleteCategory = async (projectUuid, id) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='${projectUuid}',RowKey='${id}')?${PENDENZEN_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`Azure Delete Error: ${response.status}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen der Kategorie:", error);
    throw new Error("Die Kategorie konnte nicht gelöscht werden.");
  }
};

// Alle Pendenzen über ALLE Projekte laden (ohne PartitionKey-Filter), vollständig
// paginiert. Für die projektübergreifende Ansicht "Meine Pendenzen" (US-PEND-005).
export const getAllAcrossProjects = async () => {
  try {
    await ensureTableExists();

    let allEntries = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `${getBaseUrl()}()?${PENDENZEN_CONFIG.sasToken}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Azure Error:", errorText);
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allEntries = allEntries.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allEntries
      .filter((e) => (e.RowKey || "").startsWith("pend_"))
      .map(mapEntity);
  } catch (error) {
    console.error("Fehler beim Laden aller Pendenzen:", error);
    throw new Error("Die Pendenzen konnten nicht geladen werden.");
  }
};

// Nächste fortlaufende Nummer pro Projekt ermitteln (höchste vorhandene + 1)
const getNextNummer = async (projectUuid) => {
  const existing = await getPendenzen(projectUuid);
  const maxNummer = existing.reduce(
    (max, p) => (p.nummer > max ? p.nummer : max),
    0
  );
  return maxNummer + 1;
};

// Neue Pendenz erstellen (Auto-Nummerierung)
export const createPendenz = async (projectUuid, pendenzData) => {
  try {
    await ensureTableExists();

    const rowKey = generatePendenzId();
    const now = new Date().toISOString();
    const nummer = await getNextNummer(projectUuid);

    const data = {
      ...pendenzData,
      nummer,
      status: pendenzData.status || "offen",
      erledigtAm: "",
      erstelltAm: now,
      linkedTodoIds: [],
    };

    const entity = toEntity(projectUuid, rowKey, data);

    const url = `${getBaseUrl()}?${PENDENZEN_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Save Error: ${response.status}`);
    }

    return mapEntity(entity);
  } catch (error) {
    console.error("Fehler beim Erstellen der Pendenz:", error);
    throw new Error("Die Pendenz konnte nicht gespeichert werden.");
  }
};

// Bestehende Pendenz aktualisieren (vollständiges Replace via PUT)
export const updatePendenz = async (projectUuid, pendenzId, pendenzData) => {
  try {
    const entity = toEntity(projectUuid, pendenzId, pendenzData);

    const url = `${getBaseUrl()}(PartitionKey='${projectUuid}',RowKey='${pendenzId}')?${PENDENZEN_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Azure Error Details:", errorText);
      throw new Error(`Azure Update Error: ${response.status}`);
    }

    return mapEntity(entity);
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Pendenz:", error);
    throw new Error("Die Pendenz konnte nicht aktualisiert werden.");
  }
};

// Pendenz löschen
export const deletePendenz = async (projectUuid, pendenzId) => {
  try {
    const url = `${getBaseUrl()}(PartitionKey='${projectUuid}',RowKey='${pendenzId}')?${PENDENZEN_CONFIG.sasToken}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: { "If-Match": "*" },
    });

    if (!response.ok && response.status !== 404) {
      const errorText = await response.text();
      throw new Error(`Azure Delete Error: ${response.status} - ${errorText}`);
    }
    return true;
  } catch (error) {
    console.error("Fehler beim Löschen der Pendenz:", error);
    throw new Error("Die Pendenz konnte nicht gelöscht werden.");
  }
};
